import React, { PureComponent } from 'react';
import { Checkbox, Col, InputNumber, Row } from 'antd';
import InputTime from '../../../common/input/InputTime';
import MeasureUtils from '../../../common/util/MeasureUtils';
import TimeUtils from '../../../common/util/TimeUtils';
import ConditionalRender from '../../../common/components/ConditionalRender';
import Utils from '../../../common/util/Utils';
import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { RegInitialConstraint, RegulationOrMcdmOnlyDto } from 'app/model/CORE';
import { Flex } from '../../common/Flex';
import './InitialConstraintsEditor.less';
import TitledBorder from '../../../common/panel/TitledBorder';
import AppContext from 'app/common/context/AppContext';

interface InitialConstraintsEditorProps {
    regulation: RegulationOrMcdmOnlyDto;
    onInitialConstraintsUpdate: (update: Partial<RegulationOrMcdmOnlyDto>) => void;
    readonly?: boolean;
}

interface InitialConstraintsEditorState {
    regulation: RegulationOrMcdmOnlyDto;
}

export class InitialConstraintsEditor extends PureComponent<
    InitialConstraintsEditorProps,
    InitialConstraintsEditorState
> {
    state = {
        regulation: this.props.regulation,
    };

    componentDidUpdate(
        prevProps: Readonly<InitialConstraintsEditorProps>,
        prevState: Readonly<InitialConstraintsEditorState>,
        snapshot?: any,
    ) {
        if (prevProps?.regulation !== this.props.regulation) {
            this.setState({
                regulation: this.props.regulation,
            });
        }
    }

    updateRegulation = (update: Partial<RegulationOrMcdmOnlyDto>) => {
        let updatedRegulation = Object.assign({}, this.state.regulation, update);
        updatedRegulation = MeasureUtils.adaptConstraintPeriods(
            updatedRegulation,
            Utils.isNotEmpty(update.initialConstraints),
            false,
        );
        this.setState({ regulation: updatedRegulation }, () => this.props.onInitialConstraintsUpdate(update));
    };

    updateInitialConstraint = (idx: number, props: Partial<RegInitialConstraint>) => {
        const updatedConstraint: RegInitialConstraint = Object.assign(
            {},
            this.state.regulation.initialConstraints[idx],
            props,
        );
        const newConstraints = this.state.regulation.initialConstraints.slice(); // Make a copy of the array (state mgt)
        newConstraints.splice(idx, 1, updatedConstraint);
        this.updateRegulation({ initialConstraints: newConstraints });
    };

    updateExceptionalConstraint = (checked, idx) => {
        this.updateInitialConstraint(idx, { exceptionalConstraint: checked ? {} : undefined });
    };

    updateStartTime = (startTime: Date) => {
        let from = null;

        if (startTime) {
            // Start time date has to be TODAY
            from = TimeUtils.mergeDateAndTime(AppContext.getMonitoredDay(), startTime);
        }
        this.updateRegulation({ applicability: { from, to: this.state.regulation.applicability?.to } });
    };

    removeInitialConstraint = (idx: number) => {
        let initialConstraints = this.state.regulation.initialConstraints?.slice();
        initialConstraints?.splice(idx, 1);
        if (!initialConstraints?.length) {
            initialConstraints = [{ timePeriod: {} }];
        }
        this.updateRegulation({ initialConstraints: initialConstraints });
    };

    addInitialConstraint = () => {
        const initialConstraints = this.state.regulation.initialConstraints.slice();
        initialConstraints.push({ timePeriod: {} });
        this.updateRegulation({ initialConstraints: initialConstraints });
    };

    render = () => {
        const regulation = this.state.regulation;
        const initialConstraints = regulation.initialConstraints;
        const hasExceptionalConstraints = initialConstraints?.some(constraint => constraint.exceptionalConstraint);

        return (
            <TitledBorder title="Sub Periods">
                <Flex direction="vertical" size="13em" className="initial-constraints overflow header-row">
                    <Row key={`"intial-constraint-header"`} gutter={[0, 8]}>
                        <Col span={5}>Start time</Col>
                        <Col span={4}>Normal</Col>
                        <Col span={4}>Pending</Col>
                        <Col span={2}>XCD</Col>
                        <ConditionalRender condition={hasExceptionalConstraints}>
                            <Col span={3}>
                                <span>RVR</span>
                            </Col>
                            <Col span={2}>
                                <span>FCM</span>
                            </Col>
                            <Col span={3}>
                                <span>Shift</span>
                            </Col>
                        </ConditionalRender>
                        <Col
                            span={hasExceptionalConstraints ? 1 : 9}
                            className={`add-constraint ${this.props.readonly ? 'disabled' : ''}`}>
                            <PlusOutlined onClick={() => this.addInitialConstraint()} />
                        </Col>
                    </Row>
                    {initialConstraints?.map((initialConstraint, idx) => (
                        <Row key={idx} className={`on-hover ${this.props.readonly ? 'disabled' : ''}`} gutter={[0, 8]}>
                            <Col span={5}>
                                <InputTime
                                    value={
                                        idx === 0 ? regulation.applicability?.from : initialConstraint.timePeriod?.from
                                    }
                                    onChange={value => {
                                        if (idx === 0) {
                                            this.updateStartTime(value);
                                        } else {
                                            const from = MeasureUtils.adaptTimeToPeriod(
                                                value,
                                                regulation.applicability,
                                                AppContext.getMonitoredDay(),
                                            );
                                            this.updateInitialConstraint(idx, {
                                                timePeriod: {
                                                    from,
                                                    to: initialConstraint.timePeriod?.to,
                                                },
                                            });
                                        }
                                    }}
                                />
                            </Col>
                            <Col span={4}>
                                <InputNumber
                                    min={0}
                                    value={initialConstraint.normalRate}
                                    onChange={(value: number) =>
                                        this.updateInitialConstraint(idx, { normalRate: value })
                                    }
                                />
                            </Col>
                            <Col span={4}>
                                <InputNumber
                                    min={0}
                                    value={initialConstraint.pendingRate}
                                    onChange={(value: number) =>
                                        this.updateInitialConstraint(idx, { pendingRate: value })
                                    }
                                />
                            </Col>
                            <Col span={2}>
                                <Checkbox
                                    checked={initialConstraint.exceptionalConstraint !== undefined}
                                    onChange={e => this.updateExceptionalConstraint(e.target.checked, idx)}
                                />
                            </Col>
                            <ConditionalRender condition={Utils.isExists(initialConstraint.exceptionalConstraint)}>
                                <Col span={7}>
                                    <Row>
                                        <Col span={12}>
                                            <InputNumber
                                                disabled={initialConstraint.exceptionalConstraint?.fcmMandatory}
                                                value={initialConstraint.exceptionalConstraint?.rvr}
                                                onChange={value =>
                                                    this.updateInitialConstraint(idx, {
                                                        exceptionalConstraint: {
                                                            ...initialConstraint.exceptionalConstraint,
                                                            rvr: value,
                                                        },
                                                    })
                                                }
                                            />
                                        </Col>
                                        <Col span={6}>
                                            <Checkbox
                                                checked={initialConstraint.exceptionalConstraint?.fcmMandatory}
                                                onChange={e =>
                                                    this.updateInitialConstraint(idx, {
                                                        exceptionalConstraint: {
                                                            ...initialConstraint.exceptionalConstraint,
                                                            fcmMandatory: e.target.checked,
                                                            rvr: e.target.checked
                                                                ? undefined
                                                                : initialConstraint.exceptionalConstraint.rvr,
                                                        },
                                                    })
                                                }
                                            />
                                        </Col>
                                        <Col span={6}>
                                            <Checkbox
                                                checked={initialConstraint.exceptionalConstraint?.shift}
                                                onChange={e =>
                                                    this.updateInitialConstraint(idx, {
                                                        exceptionalConstraint: {
                                                            ...initialConstraint.exceptionalConstraint,
                                                            shift: e.target.checked,
                                                        },
                                                    })
                                                }
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                            </ConditionalRender>
                            <Col className="show-on-hover remove-constraint">
                                <CloseCircleOutlined onClick={() => this.removeInitialConstraint(idx)} />
                            </Col>
                        </Row>
                    ))}
                </Flex>
            </TitledBorder>
        );
    };
}
